import React from 'react';
import { useTranslation } from 'react-i18next';

import Analytics from 'arbitrip-common/client/analytics';

import { isElalCompany, isIsracardCompany } from '@/utils/isCompany';
import { ReactComponent as OpenIcon } from '@/img/results_page/open.svg';
import { ReactComponent as ExternalLinkIcon } from '@/img/results_page/external-link.svg';

import Rm3PhotoCarousel from './Rm3PhotoCarousel';
import Rm3RoomSpecs from './Rm3RoomSpecs';
import Rm3AmenityChips from './Rm3AmenityChips';
import Rm3Card from './Rm3Card';
import { IconChevron } from './Rm3Icons';

/** The "More details" link icon, as today's room-details link: Elal `OpenIcon`, Isracard `ExternalLinkIcon`. */
function MoreDetailsIcon() {
    if (isElalCompany()) {
        return <OpenIcon />;
    }
    if (isIsracardCompany()) {
        return <ExternalLinkIcon />;
    }
    return <IconChevron size={16} className="rm3-chevron" />;
}

/**
 * One RM3 container (= a display group, the design's "room"): media, name, specs and amenities on the room
 * panel, the cards with their deal rows on the deals panel. > 1000 px one row; 701–1000 px stacked (SCSS).
 * The container has no price (spec §3.0).
 */
function Rm3RoomContainer({ container, meta, ribbons, displayOrder, context, onRoomDetails }) {
    const { t } = useTranslation();
    const { details, label } = container;
    const openDetails = details.room
        ? () => onRoomDetails(details.room, label, Analytics.actions.views.roomInfoDialog)
        : null;

    return (
        <div className="rm3-room" data-testid="rm3-room" data-display-group-key={container.key}>
            <div className="rm3-room__panel">
                <Rm3PhotoCarousel images={details.images} alt={label} onOpen={openDetails} />
                <div className="rm3-room__info">
                    <div className="rm3-room__heading">
                        <div className="rm3-room__name" data-testid="rm3-room-title">
                            <bdi>{label}</bdi>
                        </div>
                        <Rm3RoomSpecs details={details} bedText={meta.multiCard ? null : meta.singleBedText} />
                    </div>
                    {(details.amenities.length > 0 || openDetails) && (
                        <div className="rm3-room__amenities">
                            <Rm3AmenityChips amenities={details.amenities} />
                            {openDetails && (
                                <button
                                    type="button"
                                    className="rm3-more-details"
                                    onClick={openDetails}
                                    data-testid="rm3-room-more-details"
                                >
                                    {t('HotelDealsRm3.MoreDetails')}
                                    <MoreDetailsIcon />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="rm3-room__deals">
                {container.cards.map((card) => (
                    <Rm3Card
                        key={card.cardId}
                        card={card}
                        meta={meta.cards.get(card.cardId) || {}}
                        containerLabel={label}
                        showHeading={meta.multiCard}
                        ribbons={ribbons}
                        displayOrder={displayOrder}
                        context={context}
                    />
                ))}
            </div>
        </div>
    );
}

export default Rm3RoomContainer;
